// src/components/switchSuppliers/SwitchSupplierForm.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  useTheme,
  InputAdornment,
  Divider,
} from "@mui/material";
import { Close, Business, Language, Save } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { createSwitchSupplier } from "../../utils/models/switchSupplierModel";

const SwitchSupplierForm = ({ supplier, onSave, onCancel }) => {
  const theme = useTheme();
  const { add, update } = useAuditFirestore("switchSuppliers");
  const [formData, setFormData] = useState(createSwitchSupplier({}));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(supplier || createSwitchSupplier({}));
  }, [supplier]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const formatUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "El nombre es obligatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const data = {
        ...formData,
        name: formData.name.trim(),
        web: formData.web.trim(),
        updatedAt: new Date().toISOString(),
      };

      // CAMBIADO: verificar si tiene un id real (no null ni vacío)
      if (formData.id) {
        await update(formData.id, data);
      } else {
        const { id, ...dataWithoutId } = data; // Eliminar el id null
        dataWithoutId.createdAt = new Date().toISOString();
        await add(dataWithoutId);
      }
      onSave();
    } catch (error) {
      console.error("Error saving supplier:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Business sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {formData.id ? "Editar Comercializadora" : "Nueva Comercializadora"}
          </Typography>
        </Box>
        <IconButton onClick={onCancel} size="small"><Close /></IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ px: 3, pt: 4, pb: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          fullWidth
          label="Nombre de la comercializadora"
          placeholder="Ej: Iberdrola, Endesa, Naturgy..."
          value={formData.name}
          onChange={handleChange("name")}
          error={!!errors.name}
          helperText={errors.name}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Business sx={{ color: errors.name ? theme.palette.error.main : theme.palette.primary.main }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />

        <TextField
          fullWidth
          label="Página web"
          placeholder="Ej: www.iberdrola.es"
          value={formData.web}
          onChange={handleChange("web")}
          helperText="Opcional"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Language sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />

        {formData.web && (
          <Box sx={{ p: 2, borderRadius: "12px", bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : `${theme.palette.primary.main}08`, border: "1px solid", borderColor: `${theme.palette.primary.main}20` }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Vista previa de la URL:</Typography>
            <Typography variant="body2" color="primary.main" fontWeight={500} sx={{ wordBreak: "break-all" }}>
              {formatUrl(formData.web)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: "1px solid", borderColor: theme.palette.divider, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, px: 3 }}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={<Save />} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, px: 4, boxShadow: "none" }}>
          {loading ? "Guardando..." : formData.id ? "Actualizar" : "Crear Comercializadora"}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default SwitchSupplierForm;